import type { OperatorPermissions, PermissionArea } from '../../core/auth/operator-permissions.service';

export type NavItem = {
  labelKey: string;
  path: string;
  icon: string;
  /** Match only the exact path (dashboard). */
  exact?: boolean;
  /** The area the operator must be able to view for the page to be listed (and routed to). */
  permission: PermissionArea;
  /** The command palette label when the nav label needs its group to be clear ("Billing plans" vs "Plans"). */
  paletteLabelKey?: string;
};

export type NavGroup = { labelKey: string; items: NavItem[] };

export const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'admin.shell.groups.platform',
    items: [
      { labelKey: 'admin.shell.nav.dashboard', path: '/dashboard', icon: 'pi pi-chart-line', exact: true, permission: 'analytics' },
      { labelKey: 'admin.shell.nav.tenants', path: '/tenants', icon: 'pi pi-building', permission: 'tenants' },
      { labelKey: 'admin.shell.nav.operators', path: '/operators', icon: 'pi pi-users', permission: 'operators' },
    ],
  },
  {
    labelKey: 'admin.shell.groups.commerce',
    items: [
      { labelKey: 'admin.shell.nav.plans', path: '/plans', icon: 'pi pi-clone', permission: 'plans' },
      { labelKey: 'admin.shell.nav.promoCodes', path: '/promo-codes', icon: 'pi pi-ticket', permission: 'promoCodes' },
    ],
  },
  {
    labelKey: 'admin.shell.groups.billing',
    items: [
      // The billing overview and SMS conversion sit under the tenants permissions, like the tenant billing endpoints.
      {
        labelKey: 'admin.shell.nav.billingOverview', path: '/billing/overview', icon: 'pi pi-wallet', permission: 'tenants',
        paletteLabelKey: 'admin.billing.overview.title',
      },
      {
        labelKey: 'admin.shell.nav.billingPlans', path: '/billing/plans', icon: 'pi pi-tags', permission: 'plans',
        paletteLabelKey: 'admin.billing.plans.title',
      },
    ],
  },
];

/** The nav groups the operator may view, empty groups dropped. */
export function permittedNavGroups(perms: OperatorPermissions): NavGroup[] {
  return NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => perms.canView(i.permission)) })).filter((g) => g.items.length > 0);
}

export function permittedNavItems(perms: OperatorPermissions): NavItem[] {
  return permittedNavGroups(perms).flatMap((g) => g.items);
}

/** Where to land: the first page in nav order the operator may view, or null when there is none. */
export function firstPermittedPath(perms: OperatorPermissions): string | null {
  return permittedNavItems(perms)[0]?.path ?? null;
}
